import { useState, useEffect } from 'react';
import { api } from '../../../utils/api';
import EngineReadBlock from './EngineReadBlock.jsx';
import { ruleBreakdownRows } from './offerRuleView.js';

// The engine's SP multiplier per condition (getConditionTier, price-calculation.js),
// keyed by the engine tier and by the grade label in upper snake case.
const TIER_MULT = { MINT: 1.0, EXCELLENT: 1.0, VERY_GOOD: 0.9, GOOD: 0.8, FAIR: 0.5, POOR: 0.35, BROKEN: 0.2, NOT_WORKING: 0.2 };

// How the engine got from the stored Excellent baseline to this SP: the condition
// multiplier, then the SP rounding (over £1,500, nearest £10; applySpRounding in the
// pricing engine). null when the baseline does not explain the SP (a hand-set SP, the
// UK scarcity premium): a wrong figure is worse than none.
export function spChain(baseline, mult, sp) {
    if (!(baseline > 0) || mult == null) return null;
    const pre = Math.round(baseline * mult);
    const final = Math.round(Number(sp));
    if (pre > 1500 && pre !== final && Math.round(pre / 10) * 10 === final) return { pre, rounded: true };
    return Math.abs(pre - final) <= 1 ? { pre: final, rounded: false } : null;
}

// The condition step that turns the Excellent baseline into the SP, and the SP rounding.
function ConditionDowngrade({ baseline, mult, grade, sp, chain }) {
    return (
        <>
            <div className="qa-bp-row">
                <span className="qa-muted">Baseline (Excellent)</span><span>£{Math.round(baseline)}</span>
            </div>
            {mult < 1 && (
                <div className="qa-bp-row qa-bp-downgrade">
                    <span>× {mult} condition ({grade})</span><span>−£{Math.round(baseline * (1 - mult))} → £{chain.pre}</span>
                </div>
            )}
            {chain.rounded && (
                <div className="qa-bp-row">
                    <span className="qa-muted">SP rounding (to the nearest £10)</span><span>£{Math.round(Number(sp))}</span>
                </div>
            )}
        </>
    );
}

export default function BpBreakdownPanel({ dealId, quoteItemId, sp, bp, condition = null, refreshKey, signals }) {
    const [state, setState] = useState({ loading: true });
    useEffect(() => {
        let alive = true;
        setState({ loading: true });
        // The grade rides along so the offer rule's breakdown is for the item's CURRENT
        // grade, not the one it was first quoted at.
        const qs = Number(sp) > 0
            ? `?sp=${Math.round(Number(sp))}${condition ? `&condition=${encodeURIComponent(condition)}` : ''}`
            : '';
        api.get(`/api/deals/${dealId}/items/${quoteItemId}/bp-factors${qs}`)
            .then(r => { if (alive) setState({ loading: false, ...r }); })
            .catch(() => { if (alive) setState({ loading: false, available: false }); });
        return () => { alive = false; };
    }, [dealId, quoteItemId, sp, condition, refreshKey]);

    // The engine's read (signals) and the SP→BP maths come from different feeds, so the
    // read shows even while the maths is loading or its detail is missing — it must not
    // be hidden behind an unavailable price chain.
    const header = (
        <>
            <div className="qa-bp-head">🧮 Price breakdown</div>
            <EngineReadBlock signals={signals} rule={state.offerRule ?? null} counts={state.counts ?? null} />
        </>
    );
    if (state.loading) return <div className="qa-bp">{header}<div className="qa-muted">Loading breakdown…</div></div>;
    if (!state.available) return <div className="qa-bp">{header}<div className="qa-muted">SP→BP breakdown unavailable for this item.</div></div>;
    if (state.offerRule) return <div className="qa-bp">{header}<RuleBreakdown rule={state.offerRule} sp={sp} bp={bp} baselineSp={state.baselineSp} /></div>;
    if (!state.factors) return <div className="qa-bp">{header}<div className="qa-muted">SP→BP breakdown unavailable for this item.</div></div>;
    // Authoritative SP→BP chain from the pricing engine (computeBP). Rendered verbatim — no
    // client-side reconstruction, so it always matches the real BP incl. uplift + final reduction.
    const steps = Array.isArray(state.steps) ? state.steps : null;
    const tier = state.factors.conditionTier;
    const mult = TIER_MULT[tier];
    // Prefer the authoritative pre-condition baseline from fact_pricing; fall back to
    // SP ÷ mult for runs priced before the warehouse fix (baseline_sp NULL there).
    const baseline = state.baselineSp != null ? Number(state.baselineSp)
        : ((mult && mult > 0) ? Number(sp) / mult : null);
    // Old runs keep their condition step even when the sum is off (no baseline check here).
    const chain = spChain(baseline, mult, sp) ?? { pre: Math.round(Number(sp)), rounded: false };
    const showDowngrade = baseline != null && mult != null && (mult < 1 || chain.rounded);
    // The step chain is the engine's derivation from SP. A manual BP edit overrides the
    // result without changing the chain, so the item's actual BP (prop, live from
    // parsed_items) is authoritative for what we pay — surface it when it diverges.
    const chainBp = steps ? Number(steps.find(s => s.label === 'BP')?.value) : null;
    const actualBp = Number(bp);
    const manualBp = Number.isFinite(actualBp) && Number.isFinite(chainBp)
        && Math.round(actualBp) !== Math.round(chainBp);
    const bpForPct = Number.isFinite(actualBp) && actualBp > 0 ? actualBp : chainBp;
    const bpSpPct = (Number.isFinite(bpForPct) && bpForPct > 0 && Number(sp) > 0)
        ? Math.round((bpForPct / Number(sp)) * 100) : null;
    return (
        <div className="qa-bp">
            {header}
            {tier && (
                <div className="qa-bp-cond">Priced as condition: <strong>{tier}</strong> (×{mult ?? '?'})</div>
            )}
            {showDowngrade && <ConditionDowngrade baseline={baseline} mult={mult} grade={tier} sp={sp} chain={chain} />}
            {steps
                ? steps.map((s, i) => (
                    <div key={i} className={`qa-bp-row ${s.label === 'BP' ? 'qa-bp-final' : ''}`}>
                        <span className="qa-muted">{s.label}</span><span>£{s.value}</span>
                    </div>
                ))
                : <div className="qa-muted">Detailed SP→BP chain unavailable for this pricing run.</div>}
            {manualBp && (
                <div className="qa-bp-row qa-bp-final">
                    <span className="qa-muted">BP (manual override)</span><span>£{Math.round(actualBp)}</span>
                </div>
            )}
            {bpSpPct != null && (
                <div className="qa-bp-row">
                    <span className="qa-muted">BP/SP</span><span>{bpSpPct}%</span>
                </div>
            )}
        </div>
    );
}

// The offer rule's SP→BP chain, as the pricing engine returned it. A hand-set BP does not
// change the chain, so the item's actual BP is shown beside it when the two differ.
function RuleBreakdown({ rule, sp, bp, baselineSp = null }) {
    const rows = ruleBreakdownRows(rule, sp);
    // The engine cut the SP for condition before the rule ran. Show that step only when
    // the stored baseline explains this SP: a hand-set SP, a regrade to Broken (other
    // comps) or the UK scarcity premium all break the sum, and a wrong figure is worse
    // than none.
    const mult = TIER_MULT[String(rule.grade ?? '').toUpperCase().replace(/ /g, '_')];
    const baseline = baselineSp != null ? Number(baselineSp) : null;
    const chain = spChain(baseline, mult, sp);
    const showDowngrade = chain != null && (mult < 1 || chain.rounded);
    const actualBp = Number(bp);
    const manualBp = Number.isFinite(actualBp) && Math.round(actualBp) !== Math.round(rule.bp);
    const paid = Number.isFinite(actualBp) && actualBp > 0 ? actualBp : rule.bp;
    const bpSpPct = paid > 0 && Number(sp) > 0 ? Math.round((paid / Number(sp)) * 100) : null;
    return (
        <>
            <div className="qa-bp-cond">Priced as condition: <strong>{rule.grade}</strong></div>
            {showDowngrade && <ConditionDowngrade baseline={baseline} mult={mult} grade={rule.grade} sp={sp} chain={chain} />}
            {rows.map(r => (
                <div key={r.key} className={`qa-bp-row ${r.final ? 'qa-bp-final' : ''}`}>
                    <span className="qa-muted">{r.label}</span><span>£{r.value}</span>
                </div>
            ))}
            {manualBp && (
                <div className="qa-bp-row qa-bp-final">
                    <span className="qa-muted">BP (manual override)</span><span>£{Math.round(actualBp)}</span>
                </div>
            )}
            {bpSpPct != null && (
                <div className="qa-bp-row">
                    <span className="qa-muted">BP/SP</span><span>{bpSpPct}%</span>
                </div>
            )}
        </>
    );
}
